// Visionneuse PDF : pdfjs-dist (version legacy, pour les navigateurs de Baseline 2023)
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';

// pdf_viewer.mjs cherche la bibliothèque dans globalThis.pdfjsLib : une seule copie de pdf.js dans la page
globalThis.pdfjsLib = pdfjsLib;

// Worker, décodeurs wasm et polices standard : copiés dans public/ressources/pdfjs/ par l'alias pdfjs-maj
// (même version que node_modules/pdfjs-dist, sinon pdf.js signale « API version does not match Worker version »)
const RESSOURCES = '/ressources/pdfjs/';
pdfjsLib.GlobalWorkerOptions.workerSrc = RESSOURCES + 'pdf.worker.min.mjs';

const ZOOM_PAR_DEFAUT = 'page-fit'; // comme l'ancien visualiseur (DEFAULT_SCALE_VALUE)

export async function initialiserVisionneuse(section) {
  // Chargé après la déclaration de globalThis.pdfjsLib (un import statique serait exécuté avant)
  const { EventBus, PDFFindController, PDFLinkService, PDFViewer, ScrollMode } = await import('pdfjs-dist/legacy/web/pdf_viewer.mjs');

  const barre   = section.querySelector('.pdf-barre');
  const champ   = section.querySelector('.pdf-champ');
  const total   = section.querySelector('.pdf-total');
  const zoom    = section.querySelector('.pdf-zoom');
  const menu    = section.querySelector('.pdf-menu');
  const outils  = section.querySelector('[data-action="outils"]');

  const eventBus    = new EventBus();
  const linkService    = new PDFLinkService({ eventBus });
  const findController = new PDFFindController({ eventBus, linkService });

  // pdf.js amène l'occurrence à l'écran avec scrollIntoView(), qui fait aussi défiler la page du site
  // (dans l'application pdf.js, le visualiseur occupe toute la fenêtre) : on remet la fenêtre en place
  const scrollMatchIntoView = findController.scrollMatchIntoView.bind(findController);
  findController.scrollMatchIntoView = (occurrence) => {
    const { scrollX, scrollY } = window;
    scrollMatchIntoView(occurrence);
    window.scrollTo(scrollX, scrollY);
  };
  const viewer         = new PDFViewer({
    container: section.querySelector('.pdf-defilement'),
    viewer: section.querySelector('.pdfViewer'),
    eventBus,
    linkService,
    findController,
  });
  linkService.setViewer(viewer);

  // ── État de la barre d'outils ──────────────────────────────────────────────

  const majNavigation = () => {
    const page = viewer.currentPageNumber;
    champ.value = page;
    barre.querySelector('[data-action="page-precedente"]').disabled = page <= 1;
    barre.querySelector('[data-action="page-suivante"]').disabled = page >= viewer.pagesCount;
  };

  eventBus.on('pagesinit', () => {
    viewer.currentScaleValue = ZOOM_PAR_DEFAUT;
    champ.max = viewer.pagesCount;
    total.textContent = viewer.pagesCount;
    majNavigation();
  });

  eventBus.on('pagechanging', majNavigation);

  // Les composants de pdf.js ne suivent pas la taille de la zone : on réapplique les zooms relatifs
  // (automatique, page entière, pleine largeur) quand la fenêtre change de taille
  const ZOOMS_RELATIFS = ['auto', 'page-fit', 'page-width'];
  new ResizeObserver(() => {
    if (viewer.pagesCount && ZOOMS_RELATIFS.includes(viewer.currentScaleValue)) {
      viewer.currentScaleValue = viewer.currentScaleValue;
    }
  }).observe(section.querySelector('.pdf-defilement'));

  // Garde la liste du zoom à jour (boutons + et −, ou zoom choisi par pdf.js)
  eventBus.on('scalechanging', ({ scale, presetValue }) => {
    const valeur = presetValue ?? String(Math.round(scale * 100) / 100);
    const option = [...zoom.options].find((o) => o.value === valeur);
    let perso = zoom.querySelector('.pdf-zoom-perso');
    if (option) {
      if (perso) perso.hidden = true;
      zoom.value = valeur;
    } else {
      if (!perso) {
        perso = new Option('', 'perso');
        perso.className = 'pdf-zoom-perso';
        zoom.add(perso);
      }
      perso.hidden = false;
      perso.textContent = Math.round(scale * 100) + ' %';
      zoom.value = 'perso';
    }
  });

  // ── Actions ────────────────────────────────────────────────────────────────

  const fermerMenu = () => {
    menu.hidden = true;
    outils.setAttribute('aria-expanded', 'false');
  };

  const actions = {
    'page-precedente': () => viewer.previousPage(),
    'page-suivante':   () => viewer.nextPage(),
    'premiere-page':   () => { viewer.currentPageNumber = 1; fermerMenu(); },
    'derniere-page':   () => { viewer.currentPageNumber = viewer.pagesCount; fermerMenu(); },
    'zoom-arriere':    () => viewer.decreaseScale(),
    'zoom-avant':      () => viewer.increaseScale(),
    'outils':          () => {
      const ouvert = menu.hidden;
      menu.hidden = !ouvert;
      outils.setAttribute('aria-expanded', String(ouvert));
    },
  };

  section.addEventListener('click', (e) => {
    const bouton = e.target.closest('[data-action]');
    if (bouton && actions[bouton.dataset.action]) {
      actions[bouton.dataset.action]();
    }
  });

  // Ferme le menu des outils au clic ailleurs ou avec Échap
  document.addEventListener('click', (e) => {
    if (!menu.hidden && !e.target.closest('.pdf-menu, [data-action="outils"]')) fermerMenu();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !menu.hidden) {
      fermerMenu();
      outils.focus();
    }
  });

  champ.addEventListener('change', () => {
    const page = parseInt(champ.value, 10);
    if (page >= 1 && page <= viewer.pagesCount) {
      viewer.currentPageNumber = page;
    } else {
      champ.value = viewer.currentPageNumber;
    }
  });

  zoom.addEventListener('change', () => {
    if (zoom.value !== 'perso') viewer.currentScaleValue = zoom.value;
  });

  // ── Chargement du document ─────────────────────────────────────────────────

  const pdf = await pdfjsLib.getDocument({
    url: section.dataset.fichier,
    // Volumes jusqu'à 236 Mo : seulement les morceaux des pages affichées (requêtes partielles d'Apache),
    // au lieu de tout le fichier lu en continu puis complété en arrière-plan
    disableStream: true,
    disableAutoFetch: true,
    wasmUrl: RESSOURCES + 'wasm/',
    standardFontDataUrl: RESSOURCES + 'standard_fonts/',
  }).promise;

  viewer.setDocument(pdf);
  linkService.setDocument(pdf);
  findController.setDocument(pdf);

  return { viewer, eventBus, pdf, pdfjsLib, ScrollMode, fermerMenu };
}
